<template>
    <Tag
        v-tooltip.top="tooltip"
        v-bind="$attrs"
        :severity="severity"
        :icon="!isClosable ? mappedIcon : null"
        :rounded="rounded"
        :pt="pt"
        :class="[attached ? 'tw:gap-0 e-tag-attached' : 'tw:gap-1']"
    >
        <template v-if="isClosable">
            <i v-if="icon" :class="mappedIcon" class="tw:mr-1 tw:shrink-0" />
            <span class="e-tag-label">
                <slot />
            </span>
            <button
                type="button"
                data-e-dropdown-ignore-toggle
                :class="['e-tag-close-button tw:cursor-pointer tw:hover:opacity-70 tw:ml-1', closeButtonClass]"
                :aria-label="ariaCloseLabel"
                @click.stop="onClose"
                @keydown.enter.stop="onClose"
                @keydown.space.prevent.stop="onClose"
            >
                <i class="uil uil-times" />
            </button>
        </template>
        <template v-else>
            <slot />
        </template>
    </Tag>
</template>

<script>
/**
 * ETag - Abstraction wrapper around PrimeVue's Tag
 *
 * Usage:
 *   <e-tag>Draft</e-tag>
 *   <e-tag type="is-success">Published</e-tag>
 *   <e-tag type="is-danger" closable @close="onClose">Removable</e-tag>
 *   <e-tag type="is-primary" icon="check">With Icon</e-tag>
 *   <e-tag type="is-warning" icon="alert" closable @close="onClose">Icon + Closable</e-tag>
 *   <e-tag size="is-small" type="is-info">Small Tag</e-tag>
 *   <e-tag size="is-large" type="is-danger">Large Tag</e-tag>
 *   <e-tag rounded type="is-primary">Rounded Tag</e-tag>
 *   <e-tag type="is-primary is-light">Light Primary</e-tag>
 *   <e-tag type="is-success is-light">Light Success</e-tag>
 *   <e-tag type="is-danger is-light">Light Danger</e-tag>
 *   <e-tag closable close-type="is-white" type="is-danger">Custom Close Color</e-tag>
 *   <e-tag closable aria-close-label="Remove tag">Accessible Closable</e-tag>
 *   <e-tag :force-prime-vue="true" type="is-info">Force PrimeVue</e-tag>
 *
 * Notes:
 *   - Icon names are automatically translated from Buefy to Unicons (e.g., 'information' → 'info-circle')
 *   - All Buefy b-tag props, events, and slots are supported when using Buefy
 *   - Closable tags include keyboard support (Enter/Space) and proper ARIA labels
 *   - The rounded prop works with PrimeVue implementations
 *   - Light variants (is-light) are supported in PrimeVue
 *   - PrimeVue colors use the theme's default color palette and adapt to theme changes
 *   - Close button colors can be customized with closeType in PrimeVue mode
 */
import {
    bulmaTypeForPrimeVueSeverity,
    getPrimeVueSeverity,
    getUniconClass,
} from '@/components/ui/utils/componentMappings.js';

export default {
    name: 'ETag',
    inheritAttrs: false,

    props: {
        attached: {
            type: Boolean,
            default: false,
        },
        rounded: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: undefined,
        },
        icon: {
            type: String,
            default: undefined,
        },
        closable: {
            type: Boolean,
            default: false,
        },
        closeType: {
            type: String,
            default: undefined,
        },
        ariaCloseLabel: {
            type: String,
            default: 'Close',
        },
        size: {
            type: String,
            default: undefined,
        },
        tooltip: {
            type: String,
            default: undefined,
        },
    },

    emits: ['close'],

    computed: {
        isClosable() {
            return this.closable;
        },

        isLight() {
            return this.type && this.type.includes('is-light');
        },

        baseType() {
            return bulmaTypeForPrimeVueSeverity(this.type);
        },

        severity() {
            return getPrimeVueSeverity(this.baseType) || null;
        },

        mappedIcon() {
            if (!this.icon) {
                return null;
            }
            // Check if it's already a full class string containing pi or uil (looking for word boundaries)
            const hasPrefix = /\b(pi|uil)\b/.test(this.icon);
            return hasPrefix ? this.icon : getUniconClass(this.icon);
        },

        closeButtonClass() {
            if (!this.closeType) {
                return '';
            }

            const closeTypeColorMap = {
                'is-white': 'tw:text-[var(--color-white)]',
                'is-black': 'tw:text-[var(--color-black)]',
                'is-light': 'tw:text-[var(--color-grey-light)]',
                'is-dark': 'tw:text-[var(--color-grey-dark)]',
                'is-primary': 'tw:text-[var(--color-primary)]',
                'is-info': 'tw:text-[var(--color-info)]',
                'is-success': 'tw:text-[var(--color-success)]',
                'is-warning': 'tw:text-[var(--color-warning)]',
                'is-danger': 'tw:text-[var(--color-danger)]',
            };
            return closeTypeColorMap[this.closeType] || '';
        },

        pt() {
            const rootClasses = [this.getTagSizeClass(this.size), this.isClosable ? 'e-tag-closable' : '']
                .filter(Boolean)
                .join(' ');

            const iconSizeMap = {
                'is-small': 'var(--tag-icon-size-small)',
                'is-medium': 'var(--tag-icon-size-medium)',
                'is-large': 'var(--tag-icon-size-large)',
            };

            const rootStyle = {
                '--p-tag-icon-size': iconSizeMap[this.size] || 'var(--tag-icon-size-default)',
                '--p-tag-font-size': 'var(--tag-font-size-default)',
            };

            const iconClasses = [
                'tw:inline-flex',
                'tw:justify-center',
                'tw:items-center',
                'tw:relative',
                'tw:top-[0.5px]',
            ]
                .filter(Boolean)
                .join(' ');

            return this.getPassthroughOptions(rootClasses, iconClasses, rootStyle);
        },
    },

    methods: {
        onClose(event) {
            this.$emit('close', event);
        },

        getPassthroughOptions(rootClasses, iconClasses, rootStyle = {}) {
            return {
                root: {
                    class: rootClasses,
                    style: rootStyle,
                },
                icon: {
                    class: iconClasses,
                },
            };
        },

        getTagSizeClass(size) {
            const tagSizeMap = {
                'is-small': 'e-tag-size-small',
                'is-medium': 'e-tag-size-medium',
                'is-large': 'e-tag-size-large',
                small: 'e-tag-size-small',
                medium: 'e-tag-size-medium',
                large: 'e-tag-size-large',
            };

            return tagSizeMap[size] || '';
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

:root {
    --tag-font-size-default: #{$size-8};
    --tag-font-size-small: #{$size-9};
    --tag-font-size-medium: #{$size-7};
    --tag-font-size-large: #{$size-5};

    --tag-icon-size-default: #{$size-5};
    --tag-icon-size-small: #{$size-5};
    --tag-icon-size-medium: #{$size-3};
    --tag-icon-size-large: #{$size-2};

    // Colors for closeType prop (Buefy compatibility)
    --color-white: #{$white};
    --color-black: #{$black};
    --color-grey-light: #{$grey-light};
    --color-grey-dark: #{$grey-dark};
    --color-primary: #{$primary};
    --color-info: #{$info};
    --color-success: #{$success};
    --color-warning: #{$warning};
    --color-danger: #{$danger};
}

.p-tag {
    font-weight: 400;
}

.e-tag-attached :deep(> *) {
    border-radius: 0;
}

.e-tag-attached :deep(> *:first-child) {
    border-top-left-radius: $radius;
    border-bottom-left-radius: $radius;
}

.e-tag-attached :deep(> *:last-child) {
    border-top-right-radius: $radius;
    border-bottom-right-radius: $radius;
}

.e-tag-size-small {
    font-size: var(--tag-font-size-small) !important;
    padding: calc(#{$size-8} * 0.25) calc(#{$size-8} * 0.5) !important;
}

.e-tag-size-medium {
    font-size: var(--tag-font-size-medium) !important;
    padding: calc(#{$size-8} * 0.25) calc(#{$size-8} * 0.75) !important;
}

.e-tag-size-large {
    font-size: var(--tag-font-size-large) !important;
    padding: calc(#{$size-8} * 0.25) #{$size-8} !important;
}

.p-tag.e-tag-closable {
    display: inline-flex;
    align-items: center;
    max-width: min(100%, 18rem);
    min-width: 0;
}

.e-tag-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.e-tag-close-button {
    flex-shrink: 0;
}
</style>
